import { reportUnauthenticated } from './unauthenticated';

/**
 * Every candidate-journey request leaves through here, carrying the kiosk's credential.
 *
 * **`credentials: 'include'` is not an option the caller may forget** [03-us-kad-6c AC-11; decisions 0204,
 * 0221]. The venue used to travel as a `venueId` query parameter read from a hard-coded constant; it now
 * travels on an `HttpOnly` cookie the app never reads, and without this flag the browser simply does not
 * send it — every scoped route then answers 401 and the journey dies at the first call. Eleven services sent
 * no credential at all before this existed, so the flag lives in one place rather than in eleven that each
 * have to remember it.
 *
 * **And a 401 is reported from here, once, for every call** [0245]. Two of the eleven — the recruiter
 * hand-off and the I-9 notice — inspect no response at all: both end in a silent `catch`, by design [BR-K7],
 * so a refusal there is invisible to the caller and the candidate's journey looks perfect while the queue
 * entry is never written. A wrapper is the only layer that sees those two.
 *
 * ⛔ **The enrolment routes do NOT come through here**, and that is deliberate: on `POST /enrolment` a 401
 * is *that PIN was wrong* [decision 0090], not *this device is not enrolled*, and routing it to the PIN pad
 * would be a screen answering itself. They take {@link credentialedFetch} instead — the transport half
 * without the navigation half, so the credential is still never something a caller can forget.
 */
export async function kioskFetch(input: string, init?: RequestInit): Promise<Response> {
  const response = await credentialedFetch(input, init);

  // ⛔ 401 only. A 403 is a scoped refusal and a 503 is the store not answering; neither unenrols anything.
  if (response.status === 401) {
    /*
     * ⚠️ **The app's only reporting channel** — there is no telemetry sink and no error reporter in `src/`,
     * and `no-console` allows exactly `warn` and `error`. Same shape as `arrivalReason.ts`'s refusal alert
     * (`msg` · `alert` · …), so one format reads from both. ⛔ **No PIN, no candidate id, no factor**: this
     * fires on a shared device's console, and the handler it triggers has just wiped a stranger's last-4
     * SSN and birthday. Without it, the single most consequential thing this app does — clearing a session
     * and returning a staff-facing pad to whoever is standing there — leaves no trace at all.
     */
    console.error({
      msg: 'the api REFUSED this device\u2019s credential — the candidate session was cleared and the app returned to the PIN pad',
      alert: 'kiosk_credential_refused',
      path: routePattern(input),
    });
    reportUnauthenticated();
  }

  return response;
}

/**
 * The route a request took, never the request itself: **every all-digit path segment becomes `:id`.**
 *
 * ⛔ **The path is where the candidate id travels** — `/candidates/:id/profile` and `/associate/:id/status`
 * both carry the Bullhorn id, a `number` at both call sites, so the raw pathname put a stranger's id on a
 * shared console. ⭐ **Only whole segments**: `/i9-notice` has a digit and is a route, not an id. The query
 * string and the host never reach the line at all. ⚠️ This covers the app's own line only: the browser's network
 * entry for the same failed request still prints the full URL, and only moving the id off it closes that.
 */
function routePattern(input: string): string {
  return new URL(input, 'http://kiosk.invalid').pathname
    .split('/')
    .map((segment) => (/^\d+$/.test(segment) ? ':id' : segment))
    .join('/');
}

/**
 * The transport half, alone: **the credential, on every call, and no opinion about the answer.**
 *
 * ⭐ **Split out because the two policies have different customers.** Every candidate-journey call wants
 * both halves and uses {@link kioskFetch}. The enrolment routes want ONLY this one — on `POST /enrolment` a
 * 401 is *that PIN was wrong* [0090], not *this device is not enrolled* — and before the split they had to
 * hand-roll `credentials: 'include'` twice, in the module where forgetting it is most expensive: the PIN
 * screen's own calls. ⛔ **KAD-6d's scoped `PUT /enrolment/name` would have been the third.**
 */
export async function credentialedFetch(input: string, init?: RequestInit): Promise<Response> {
  return fetch(input, { ...init, credentials: 'include' });
}
